import PropTypes from "prop-types";
import React from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { FileScanStatus, FileScanStatusToMessage } from "@/utils/fileUtil";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { AlertIcon, DocumentIcon } from "../../Icons";
import { createClassNameString, generateRandomId } from "../../utils";
import { PageBreak } from "../../PageBreak";
import { Popover } from "../../Popover";
import { Text } from "../../Text";
import { TextTypes } from "../../constants";

import { InsuranceField } from "./_/InsuranceField";

import "./styles.scss";

export function CompletedProfileInsurance({
  className,
  groupId,
  insuranceCardImgBackSrc,
  insuranceCardImgBackStatus,
  insuranceCardImgFrontSrc,
  insuranceCardImgFrontStatus,
  insuranceCompany,
  isAso,
  isEditable,
  memberId,
  onEditButtonClick,
  onInsuranceCardImgBackClick,
  onInsuranceCardImgFrontClick,
  policyHolder,
  policyHolderDOB,
  policyHolderName,
  stateOfInsurancePlan,
  title,
  ...props
}) {
  const renderImageThumbnail = (imageSource, imageStatus, onClick) => {
    const tooltipId = `${generateRandomId()}-tooltip`;
    const dataTip = FileScanStatusToMessage[imageStatus];

    return (
      <>
        {(!imageStatus || imageStatus === FileScanStatus.READY) && imageSource && (
          <img
            className="apti-CompletedProfileInsurance--image"
            onClick={onClick}
            src={imageSource}
          />
        )}

        {imageStatus === FileScanStatus.PENDING && (
          <>
            <span
              className="apti-CompletedProfileInsurance--tooltip-icon"
              data-for={tooltipId}
              data-tip={dataTip}
            >
              <DocumentIcon />
            </span>

            <Popover className="apti-Info--popover" position="top" toolTipId={tooltipId}>
              {dataTip}
            </Popover>
          </>
        )}

        {imageStatus === FileScanStatus.ERROR && (
          <>
            <span
              className="apti-CompletedProfileInsurance--tooltip-icon"
              data-for={tooltipId}
              data-tip={dataTip}
            >
              <AlertIcon />
            </span>

            <Popover className="apti-Info--popover" position="top" toolTipId={tooltipId}>
              {dataTip}
            </Popover>
          </>
        )}
      </>
    );
  };

  return (
    <div
      className={createClassNameString(["apti-CompletedProfileInsurance", className])}
      {...props}
    >
      <div className="apti-CompletedProfileInsurance--header">
        <Text type={TextTypes.heading2}>{title}</Text>

        {isEditable && (
          <Button2
            onPress={onEditButtonClick}
            size="sm"
            variant="text"
            {...getAutomationDataAttr("insurance-edit-button")}
          >
            Edit
          </Button2>
        )}
      </div>

      <PageBreak />

      <div className="apti-CompletedProfileInsurance--body">
        <div className="apti-CompletedProfileInsurance--left">
          <InsuranceField title="Insurance Company">{insuranceCompany}</InsuranceField>
          <InsuranceField title="Member ID">{memberId}</InsuranceField>

          <InsuranceField title="Insurance Card Images">
            <div className="apti-CompletedProfileInsurance--image-container" data-private="true">
              {renderImageThumbnail(
                insuranceCardImgFrontSrc,
                insuranceCardImgFrontStatus,
                onInsuranceCardImgFrontClick,
              )}

              {renderImageThumbnail(
                insuranceCardImgBackSrc,
                insuranceCardImgBackStatus,
                onInsuranceCardImgBackClick,
              )}
            </div>
          </InsuranceField>

          <InsuranceField title="Policy Holder Name">{policyHolderName}</InsuranceField>
        </div>

        <div className="apti-CompletedProfileInsurance--right">
          <InsuranceField title="Policy Holder">{policyHolder}</InsuranceField>
          <InsuranceField title="Group ID">{groupId}</InsuranceField>

          {isAso !== null && (
            <InsuranceField title="Is ASO?">{isAso ? "yes" : "no"}</InsuranceField>
          )}

          <InsuranceField title="State of Insurance Plan">{stateOfInsurancePlan}</InsuranceField>
          <InsuranceField title="Policy Holder Date of Birth">{policyHolderDOB}</InsuranceField>
        </div>
      </div>
    </div>
  );
}

CompletedProfileInsurance.propTypes = {
  className: PropTypes.string,
  groupId: PropTypes.string,

  insuranceCardImgBackSrc: PropTypes.string,
  insuranceCardImgBackStatus: PropTypes.string,
  insuranceCardImgFrontSrc: PropTypes.string,
  insuranceCardImgFrontStatus: PropTypes.string,
  insuranceCompany: PropTypes.string.isRequired,
  isAso: PropTypes.bool,
  isEditable: PropTypes.bool.isRequired,
  memberId: PropTypes.string.isRequired,

  onEditButtonClick: PropTypes.func.isRequired,
  onInsuranceCardImgBackClick: PropTypes.func,
  onInsuranceCardImgFrontClick: PropTypes.func,

  policyHolder: PropTypes.string.isRequired,
  policyHolderDOB: PropTypes.string.isRequired,
  policyHolderName: PropTypes.string.isRequired,

  stateOfInsurancePlan: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

CompletedProfileInsurance.defaultProps = {
  className: undefined,
  groupId: "",

  insuranceCardImgBackSrc: null,
  insuranceCardImgBackStatus: null,
  insuranceCardImgFrontSrc: null,
  insuranceCardImgFrontStatus: null,
  insuranceCompany: "",
  isAso: false,
  isEditable: true,
  memberId: "",

  onEditButtonClick: "",
  onInsuranceCardImgBackClick: () => {},
  onInsuranceCardImgFrontClick: () => {},

  policyHolder: "",
  policyHolderDOB: "",
  policyHolderName: "",

  stateOfInsurancePlan: "",
  title: "",
};
